import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Receipt, Loader2, Mail, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGIN_BG = "https://images.pexels.com/photos/20314945/pexels-photo-20314945.jpeg";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Restore destination preserved by ProtectedRoute (e.g. email deep-links to a conversation)
  const from = location.state?.from || "/";

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(from, { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Form side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
              <Receipt className="h-5 w-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-semibold text-slate-900 text-lg tracking-tight">Tax Flow</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Expat Tax Firm</div>
            </div>
          </div>

          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="text-slate-500 text-sm mt-1.5 mb-8">Sign in to your Intelligent Tax Operating System.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@expattaxfirm.com"
                  data-testid="login-email"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-xs text-slate-400 mt-8 text-center">
            Secured with role-based access control &amp; audit logging.
          </p>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/80 via-blue-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-semibold tracking-tight leading-tight">
            The Intelligent Tax Operating System
          </h2>
          <p className="text-slate-200/90 mt-3 max-w-md leading-relaxed">
            Sales, client success, production, documents and finance — unified, automated, and powered by Penny AI.
          </p>
        </div>
      </div>
    </div>
  );
}
